import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { Dispatch, SetStateAction, useState } from "react";
import AnimateHeight from "react-animate-height";
import Input from "../../../../../../components/Input/Input";
import MuiSwitch from "../../../../../../components/MuiSwitch/MuiSwitch";
import RadioButtonsGroup from "../../../../../../components/RadioGroup/RadioGroup";
import { Project } from "../../../../../../types/Company";
import {
  ProjectsAvailableSessionTypes,
  TypeNewProject,
} from "../../../../../../types/Projects";
import { SessionPackage } from "../../../../../../types/TypeSession";
import ProjectPackage from "../ProjectPackage/ProjectPackage";
import s from "./ProjectPricing.module.css";

export type ProjectLevelOption = {
  value: string;
  lable: string;
  specializationId: string;
  specializationLabel: string;
};

type ProjectPricingProps = {
  pricingType: "sessions" | "packages";
  setNewProgramData: Dispatch<SetStateAction<TypeNewProject>>;
  newProgramData: TypeNewProject;
  levels: ProjectLevelOption[];
  staticPackages: SessionPackage[];
  programData?: Project;
};

type PricingField = "price" | "discount" | "tax";

const pricingConfig = {
  sessions: {
    title: "Session setup",
    projectType: ProjectsAvailableSessionTypes.SESSION,
    pricingKey: "pricing",
    staticKey: "staticSessionPrices",
    emptyMessage: "Select specializations with levels to configure sessions.",
  },
  packages: {
    title: "Package setup",
    projectType: ProjectsAvailableSessionTypes.PACKAGE,
    pricingKey: "packagePricing",
    staticKey: "staticPackagePrices",
    emptyMessage: "Select specializations with levels to configure packages.",
  },
} as const;

const modeOptions = [
  { value: "discount", lable: "Discount" },
  { value: "price", lable: "Fixed price" },
];

const ProjectPricing = ({
  pricingType,
  setNewProgramData,
  newProgramData,
  levels,
  programData,
  staticPackages,
}: ProjectPricingProps) => {
  const [isOpen, setIsOpen] = useState(true);
  const [collapsedSpecializations, setCollapsedSpecializations] = useState<
    Record<string, boolean>
  >({});
  const config = pricingConfig[pricingType];
  const pricing = newProgramData[config.pricingKey] ?? [];
  const limits = newProgramData.availableSessionTypes ?? [];
  const groupedLevels = levels.reduce<
    Record<string, { label: string; levels: ProjectLevelOption[] }>
  >(
    (acc, level) => {
      const key = level.specializationId;
      acc[key] = {
        label: level.specializationLabel || "Specialization",
        levels: [...(acc[key]?.levels ?? []), level],
      };
      return acc;
    },
    {},
  );
  const selectedSpecializations = Object.values(
    levels.reduce<
      Record<
        string,
        { mainSpecializationRef: string; specialization: string }
      >
    >((acc, level) => {
      acc[level.specializationId] = {
        mainSpecializationRef: level.specializationId,
        specialization: level.specializationLabel,
      };
      return acc;
    }, {}),
  );

  const updatePricing = (
    levelId: string,
    field: PricingField,
    value?: number,
  ) => {
    setNewProgramData((prev) => {
      const pricingKey = config.pricingKey;
      const currentPricing = prev[pricingKey] ?? [];
      const currentItem = currentPricing.find((item) => item.level === levelId);
      const nextItem = {
        level: levelId,
        ...currentItem,
        [field]: value,
      };

      if (field === "price" && value !== undefined) {
        delete nextItem.discount;
      }
      if (field === "discount" && value !== undefined) {
        delete nextItem.price;
      }

      const hasPricingValue =
        nextItem.price !== undefined ||
        nextItem.discount !== undefined ||
        nextItem.tax !== undefined;

      return {
        ...prev,
        [pricingKey]: hasPricingValue
          ? [
              ...currentPricing.filter((item) => item.level !== levelId),
              nextItem,
            ]
          : currentPricing.filter((item) => item.level !== levelId),
        [config.staticKey]: true,
      };
    });
  };

  const updateLimit = (
    specializationId: string,
    values: Partial<{ amount: number; unlimited: boolean }>,
  ) => {
    setNewProgramData((prev) => ({
      ...prev,
      availableSessionTypes: (prev.availableSessionTypes ?? []).map((item) =>
        item.type === config.projectType &&
        item.specialization === specializationId
          ? { ...item, ...values }
          : item,
      ),
    }));
  };

  const getPricingItem = (levelId: string) =>
    pricing.find((item) => item.level === levelId);

  const getLimitItem = (specializationId: string) =>
    limits.find(
      (item) =>
        item.type === config.projectType &&
        item.specialization === specializationId,
    );

  const getMode = (levelId: string) =>
    getPricingItem(levelId)?.price !== undefined ? "price" : "discount";

  const getNumericValue = (value: number | undefined) =>
    value === undefined ? "" : value.toString();

  const parsePercent = (value: string) => {
    if (value === "") return undefined;
    const numberValue = Number(value);
    if (!Number.isFinite(numberValue)) return undefined;
    return Math.min(Math.max(numberValue, 0), 100);
  };

  const parseAmount = (value: string) => {
    if (value === "") return 0;
    const numberValue = Number(value);
    return Number.isInteger(numberValue) && numberValue >= 0
      ? numberValue
      : undefined;
  };

  const toggleSpecialization = (specialization: string) => {
    setCollapsedSpecializations((prev) => ({
      ...prev,
      [specialization]: !prev[specialization],
    }));
  };

  return (
    <div className={s.container}>
      <button
        type="button"
        className={s.header}
        onClick={() => setIsOpen((prev) => !prev)}
        aria-expanded={isOpen}
      >
        <div className={s.headerText}>
          <span className={s.title}>{config.title}</span>
          <span className={s.subtitle}>
            Configure pricing by level and purchase limits by specialization.
          </span>
        </div>
        <KeyboardArrowDownIcon
          className={`${s.collapseIcon} ${isOpen ? s.collapseIconOpen : ""}`}
        />
      </button>

      <AnimateHeight
        height={isOpen ? "auto" : 0}
        duration={260}
        easing="ease-in-out"
      >
        {!levels.length ? (
          <div className={s.emptyState}>{config.emptyMessage}</div>
        ) : (
          <div className={s.specializationList}>
            {Object.entries(groupedLevels).map(
              ([specializationId, specializationData]) => {
                const { label: specialization, levels: specLevels } =
                  specializationData;
                const specializationIsOpen =
                  !collapsedSpecializations[specializationId];
                const limitItem = getLimitItem(specializationId);
                const limitAmount = limitItem?.amount ?? 0;
                const unlimited = limitItem?.unlimited ?? true;

                return (
                  <section
                    className={s.specializationBlock}
                    key={specializationId}
                  >
                    <button
                      type="button"
                      className={s.specializationHeader}
                      onClick={() => toggleSpecialization(specializationId)}
                      aria-expanded={specializationIsOpen}
                    >
                      <h3>{specialization}</h3>
                      <KeyboardArrowDownIcon
                        className={`${s.collapseIcon} ${
                          specializationIsOpen ? s.collapseIconOpen : ""
                        }`}
                      />
                    </button>
                    <AnimateHeight
                      height={specializationIsOpen ? "auto" : 0}
                      duration={240}
                      easing="ease-in-out"
                    >
                      <div className={s.limitRow}>
                        <Input
                          type="number"
                          inputValue={limitAmount.toString()}
                          onChangeInput={(value) => {
                            const parsed = parseAmount(value);
                            if (parsed !== undefined) {
                              updateLimit(specializationId, {
                                amount: parsed,
                              });
                            }
                          }}
                          isVisible
                          required={false}
                          lable="Purchase limit"
                          minNumber={0}
                          maxNumber={9999}
                          disabled={unlimited}
                        />
                        <MuiSwitch
                          lable="Unlimited"
                          lablePlacement="start"
                          checked={unlimited}
                          setChecked={(value: boolean) =>
                            updateLimit(specializationId, {
                              unlimited: value,
                              amount: value ? 0 : limitAmount,
                            })
                          }
                        />
                      </div>
                      <div className={s.levelRows}>
                        {specLevels.map((level) => {
                          const pricingItem = getPricingItem(level.value);
                          const mode = getMode(level.value);

                          return (
                            <div className={s.levelRow} key={level.value}>
                              <div className={s.levelInfo}>
                                <span>{level.lable}</span>
                              </div>
                              <div className={s.modeBlock}>
                                <RadioButtonsGroup
                                  data={modeOptions}
                                  defaultValue="discount"
                                  value={mode}
                                  width="100%"
                                  onChangeFunction={(value) => {
                                    const currentValue =
                                      value === "price"
                                        ? pricingItem?.discount
                                        : pricingItem?.price;
                                    updatePricing(
                                      level.value,
                                      value === "price" ? "price" : "discount",
                                      currentValue ?? 0,
                                    );
                                  }}
                                />
                              </div>
                              <Input
                                type="number"
                                inputValue={
                                  mode === "price"
                                    ? getNumericValue(pricingItem?.price)
                                    : getNumericValue(pricingItem?.discount)
                                }
                                onChangeInput={(value) => {
                                  const parsed =
                                    mode === "price"
                                      ? parseAmount(value)
                                      : parsePercent(value);
                                  updatePricing(
                                    level.value,
                                    mode === "price" ? "price" : "discount",
                                    parsed,
                                  );
                                }}
                                isVisible
                                required={false}
                                lable={
                                  mode === "price"
                                    ? "Price ($)"
                                    : "Discount (%)"
                                }
                                minNumber={0}
                                maxNumber={
                                  mode === "discount" ? 100 : undefined
                                }
                              />
                              <Input
                                type="number"
                                inputValue={getNumericValue(pricingItem?.tax)}
                                onChangeInput={(value) =>
                                  updatePricing(
                                    level.value,
                                    "tax",
                                    parsePercent(value),
                                  )
                                }
                                isVisible
                                required={false}
                                lable="Tax (%)"
                                minNumber={0}
                                maxNumber={100}
                              />
                            </div>
                          );
                        })}
                      </div>
                    </AnimateHeight>
                  </section>
                );
              },
            )}
          </div>
        )}

        {pricingType === "packages" &&
          newProgramData.staticPackagePrices &&
          (programData ? (
            <div className={s.packagesBlock}>
              {selectedSpecializations.map((specialization) => (
                <ProjectPackage
                  specialization={specialization}
                  projectId={programData._id}
                  staticPackages={staticPackages}
                  key={specialization.mainSpecializationRef}
                />
              ))}
            </div>
          ) : (
            <div className={s.createPackageMesssage}>
              You can add static session sets after creating a project in edit
              mode.
            </div>
          ))}
      </AnimateHeight>
    </div>
  );
};

export default ProjectPricing;
